import { css } from '@emotion/react';
import {
  type HTMLAttributes, useEffect, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { RadioComponent } from '~/_shared/baseComponents/radio/radio.component';
import { RectangleTag } from '~/_shared/baseComponents/rectangleTag';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { createColor } from '~/_shared/components/colorPicker/colorPicker.helpers';
import {
  MapOverlayDividerComponent, MapOverlayPanelComponent,
} from '~/_shared/components/mapOverlay';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type { Theme } from '~/_shared/themes/theme.model';
import { getContrastColor } from '~/_shared/utils/colors/colors.helpers';
import { delay } from '~/_shared/utils/delay';
import { scrollIntoViewIfNecessary } from '~/_shared/utils/dom/dom.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import type { BoundaryId } from '~/store/boundaries/boundaryIdentifier.type';
import { selectedBucketChanged } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.actionCreators';
import type { BoundarySelectBucket } from '~/store/frontendState/mapTools/boundary/boundarySelect/boundarySelect.state';

type EditedBucketSelectorProps = Readonly<{
  ref?: React.Ref<HTMLDivElement>;
  className?: string;
  /**
   * The currently selected bucket ID, or null if "Remove from all" selected.
   */
  selectedBucketId: BoundaryId | null;
  buckets: readonly BoundarySelectBucket[];
}>;

const wrapperStyles = css([{
  maxWidth: 200,
  minWidth: 0,
}]);

const headerStyles = (theme: Theme) => css({
  background: theme.iconColors.backgroundPrimary,
  color: theme.textColors.primary,
  paddingBlock: 4,
  paddingInline: 8,
  fontSize: 16,
});

const contentStyles = css({
  padding: 4,
  paddingTop: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  fontSize: 12,
});

const radioStyles = css({
  whiteSpace: 'nowrap',
});

const removeFromAllRadioStyles = css({
  padding: 4,
  fontSize: 12,
});

const underRemoveFromAllDividerStyles = css({
  marginBottom: -4,
});

const ellipsisStyles = css({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
});

export const EditedBucketSelector = ({
  selectedBucketId,
  buckets,
  ...restProps
}: EditedBucketSelectorProps & HTMLAttributes<HTMLDivElement>) => {
  const [t] = useTranslation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const [selectedBucketElement, setSelectedBucketElement] = useState<HTMLDivElement | null>(null);

  useEffect(() => {
    if (selectedBucketElement) {
      const delayedScroll = delay(100).then(() => {
        scrollIntoViewIfNecessary(selectedBucketElement, { block: 'nearest' });
      });

      return () => {
        delayedScroll.cancel();
      };
    }
    return;
  }, [selectedBucketElement]);

  return (
    <MapOverlayPanelComponent
      scrollArrowPlacement="bottom"
      css={wrapperStyles}
      staticTop={(
        <>
          <div css={headerStyles}>
            {t('quickEdit.territorySelector.heading')}
          </div>

          <RadioComponent
            css={[radioStyles, removeFromAllRadioStyles]}
            isChecked={!selectedBucketId}
            onChange={() => dispatch(selectedBucketChanged(null))}
            value="RemoveFromAll"
            label={t('Remove from all')}
          />
          <MapOverlayDividerComponent
            css={underRemoveFromAllDividerStyles}
          />
        </>
      )}
      content={(
        <div css={contentStyles}>
          {buckets?.map((bucket, index) => {
            const backgroundColor = bucket.colorInfo.fillColor;
            const color = getContrastColor(createColor(theme.textColors.primary), createColor(backgroundColor)).hex;
            const isChecked = bucket.bucketId === selectedBucketId;

            return (
              <RadioComponent
                key={`${index}|${bucket.bucketId}`}
                ref={isChecked ? setSelectedBucketElement : undefined}
                css={radioStyles}
                isChecked={isChecked}
                onChange={() => dispatch(selectedBucketChanged(bucket.bucketId))}
                value={`${bucket.bucketId}`}
                label={(
                  <TooltipComponent
                    tooltipContent={bucket.name}
                    behavior={TooltipBehavior.ShowOnOverflow}
                  >
                    <RectangleTag
                      css={ellipsisStyles}
                      style={{
                        backgroundColor,
                        color,
                      }}
                    >
                      {bucket.name}
                    </RectangleTag>
                  </TooltipComponent>
                )}
              />
            );
          })}
        </div>
      )}
      {...restProps}
    />
  );
};
